import { FormEvent, useCallback, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { ControlledTextInput } from "@/components/FormInputs";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

export const Login = () => {
    const { signIn, authUser } = useAuth();
    const { search } = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);

    const onSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            try {
                await signIn(email, password);
            } catch (e: unknown) {
                if (e instanceof Error && "code" in e) {
                    let errorMessage: string;

                    const message = (e as { code: string }).code;

                    switch (message) {
                        case "auth/invalid-credential":
                            errorMessage = "Invalid email or password. Please try again.";
                            break;
                        default:
                            errorMessage = "An error occurred during sign in. Please try again.";
                    }

                    setError(errorMessage);
                } else {
                    setError("An unexpected error occurred. Please try again.");
                }
            }
        },
        [email, password, signIn]
    );

    if (authUser) {
        const nextUrl = findNextSearchParam(search) ?? "/";
        return <Navigate to={nextUrl} replace />;
    }

    return (
        <div className="flex h-screen flex-col items-center justify-center">
            <form className="form-styling m-auto flex flex-col gap-4" onSubmit={onSubmit}>
                <ControlledTextInput
                    id="email"
                    onValue={setEmail}
                    value={email}
                    label="Email"
                    type="email"
                    placeholder="Email"
                />
                <ControlledTextInput
                    id={password}
                    onValue={setPassword}
                    value={password}
                    label="Password"
                    type="password"
                    placeholder="Password"
                />
                {error != null ? (
                    <span className="error text-red ml-4 inline-flex h-full items-center">
                        {error}
                    </span>
                ) : null}

                <Button className="bg-blue-50" name={"Sign in"} type="submit">
                    Sign in
                </Button>
            </form>
        </div>
    );
};

function findNextSearchParam(search: string): string | null {
    if (!search) {
        return null;
    }

    const next = search
        .slice(1)
        .split("&")
        .map(segment => segment.split("=", 2))
        .find(([key]) => key === "next")?.[1];

    const nextPath = decodeURIComponent(next ?? "");

    if (!nextPath.startsWith("/")) {
        return null;
    }

    return nextPath;
}
